import { useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import currency from "currency.js";
import styled from "styled-components";
import { ITableColumn } from "../Table/TableColumn";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { TableWrapper, TableLoader, TableHeaderCell, Align } from "../Table/style";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { IPerpDataOverviewRow } from "../../utils/perpDataOverview";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";

const getDeltaColor = (value: number) => {
  if (value > 0) return COLORS.green.one;
  if (value < 0) return COLORS.red.one;
  return TEXT_COLORS.one;
};

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${SPACING.four}px;
  font-size: 13px;
  color: ${TEXT_COLORS.two};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.three}px;
`;

interface IPerpDataOverviewTableProps {
  rows: IPerpDataOverviewRow[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSelectAsset: (asset: string) => void;
}

const formatNotional = (value: number) => {
  if (Math.abs(value) < 0.01) return "—";
  return currency(value).format();
};

export function PerpDataOverviewTable({
  rows,
  isLoading,
  page,
  pageSize,
  onPageChange,
  onSelectAsset,
}: IPerpDataOverviewTableProps) {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const columns: ITableColumn<IPerpDataOverviewRow>[] = useMemo(
    () => [
      {
        title: "Asset",
        accessor: "asset",
        align: "left",
        Cell: ({ value }) => (
          <span style={{ fontWeight: 600, color: COLORS.blue.one }}>{value}</span>
        ),
      },
      {
        title: "Net Delta (USD)",
        accessor: "netDeltaUsd",
        align: "right",
        Cell: ({ row }) => (
          <Align align="right" style={{ color: getDeltaColor(row.original.netDelta) }}>
            {currency(row.original.netDeltaUsd).format()}
          </Align>
        ),
      },
      {
        title: "Aevo",
        accessor: "aevoNotionalUsd",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(Number(value)) }}>
            {formatNotional(Number(value))}
          </Align>
        ),
      },
      {
        title: "Binance",
        accessor: "binanceNotionalUsd",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(Number(value)) }}>
            {formatNotional(Number(value))}
          </Align>
        ),
      },
      {
        title: "Bybit",
        accessor: "bybitNotionalUsd",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(Number(value)) }}>
            {formatNotional(Number(value))}
          </Align>
        ),
      },
      {
        title: "Hyperliquid",
        accessor: "hyperliquidNotionalUsd",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(Number(value)) }}>
            {formatNotional(Number(value))}
          </Align>
        ),
      },
      {
        title: "Lighter Robinhood",
        accessor: "lighterNotionalUsd",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(Number(value)) }}>
            {formatNotional(Number(value))}
          </Align>
        ),
      },
      {
        title: "Lighter Mainnet",
        accessor: "lighterMainnetNotionalUsd",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getDeltaColor(Number(value)) }}>
            {formatNotional(Number(value))}
          </Align>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows: tableRows, prepareRow } =
    useTable(
      {
        columns,
        data: pageRows,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
      },
      useSortBy
    );

  return (
    <div>
      <TableWrapper>
        <TableLoader isLoading={isLoading} />
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column: any) => (
                  <TableHeaderCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                  >
                    {column.render("Header")}
                  </TableHeaderCell>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {tableRows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  onClick={() => onSelectAsset(row.original.asset)}
                  style={{ cursor: "pointer" }}
                >
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} key={cell.column.id}>
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
            {!isLoading && tableRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  No perp assets found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>

      <PaginationRow>
        <span>
          {rows.length === 0
            ? "0 assets"
            : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, rows.length)} of ${rows.length}`}
        </span>
        <PaginationControls>
          <Button
            buttonTheme={ButtonThemeEnum.HIGHLIGHT}
            type="button"
            disabled={page <= 1 || isLoading}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <span style={{ color: TEXT_COLORS.one }}>
            Page {page} / {totalPages}
          </span>
          <Button
            buttonTheme={ButtonThemeEnum.HIGHLIGHT}
            type="button"
            disabled={page >= totalPages || isLoading}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </PaginationControls>
      </PaginationRow>
    </div>
  );
}
